import React, { createContext, useEffect, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { ClimaCheckOptionsType, LandscapeFeatureType, SpecialEcologicalArea } from '~/api/services/climaCheck'
import { StyledButtonGroup, StyledButtonOr, StyledTabButton, TabContainer, TabContent } from '../../../../Co2Climate/AnswerCO2Climate/Steps/GrassSilageStep/style'
import AllgemeinStep from './AllgemeinStep'
import OekologieStep from './OekologieStep'
import SozialesStep from './SozialesStep'
import TierwohlStep from './TierwohlStep'

export interface SummaryOptionsContextType {
  milkingTechnologies: ClimaCheckOptionsType['milkingTechnologies']
  landscapeFeatureTypes: LandscapeFeatureType[]
  specialEcologicalAreas: SpecialEcologicalArea[]
  daysOffPerWeekOptions: ClimaCheckOptionsType['daysOffPerWeekOptions']
  vacationPerYearOptions: ClimaCheckOptionsType['vacationPerYearOptions']
  workloadOptions: ClimaCheckOptionsType['workloadOptions']
  voluntaryEngagementOptions: ClimaCheckOptionsType['voluntaryEngagementOptions']
  dryOffStrategyOptions: ClimaCheckOptionsType['dryOffStrategyOptions']
  mastitisAntibioticsUseOptions: ClimaCheckOptionsType['mastitisAntibioticsUseOptions']
  hoofCareFrequencyOptions: ClimaCheckOptionsType['hoofCareFrequencyOptions']
}

export const SummaryOptionsContext = createContext<SummaryOptionsContextType>(null)

interface Props {
  options: ClimaCheckOptionsType
}

const SummaryStep: React.FC<Props> = ({ options }) => {
  const { control } = useFormContext()
  const summaryStep = useWatch({ control, name: 'additionalQuestions.step' })

  const [activeStep, setActiveStep] = useState(0)
  const [isTransitioning, setIsTransitioning] = useState(false)

  const contextValue: SummaryOptionsContextType = {
    milkingTechnologies: options?.milkingTechnologies || [],
    landscapeFeatureTypes: options?.landscapeFeatureTypes || [],
    specialEcologicalAreas: options?.specialEcologicalAreas || [],
    daysOffPerWeekOptions: options?.daysOffPerWeekOptions || [],
    vacationPerYearOptions: options?.vacationPerYearOptions || [],
    workloadOptions: options?.workloadOptions || [],
    voluntaryEngagementOptions: options?.voluntaryEngagementOptions || [],
    dryOffStrategyOptions: options?.dryOffStrategyOptions || [],
    mastitisAntibioticsUseOptions: options?.mastitisAntibioticsUseOptions || [],
    hoofCareFrequencyOptions: options?.hoofCareFrequencyOptions || []
  }

  function renderSteps() {
    switch (activeStep) {
      case 0:
        return <AllgemeinStep />
      case 1:
        return <OekologieStep />
      case 2:
        return <SozialesStep />
      case 3:
        return <TierwohlStep />
      default:
        return <AllgemeinStep />
    }
  }

  useEffect(() => {
    handleTabChange(summaryStep || 0)
  }, [summaryStep])

  const handleTabChange = async (newStep: number) => {
    if (newStep === activeStep) return

    setIsTransitioning(true)
    await new Promise(resolve => setTimeout(resolve, 200))
    setActiveStep(newStep)
    setTimeout(() => setIsTransitioning(false), 200)
  }

  return (
    <SummaryOptionsContext.Provider value={contextValue}>
      <TabContainer>
        <StyledButtonGroup widths={'4'} size={'large'}>
          <StyledTabButton
            className={activeStep === 0 ? 'active-step' : ''}
            type={'button'}
            content={'Allgemein'}
            disabled={isTransitioning}
            onClick={() => handleTabChange(0)}
          />
          <StyledButtonOr text='>>' />
          <StyledTabButton
            className={activeStep === 1 ? 'active-step' : ''}
            type={'button'}
            content={'Ökologie'}
            disabled={isTransitioning}
            onClick={() => handleTabChange(1)}
          />
          <StyledButtonOr text='>>' />
          <StyledTabButton
            className={activeStep === 2 ? 'active-step' : ''}
            type={'button'}
            content={'Soziales'}
            disabled={isTransitioning}
            onClick={() => handleTabChange(2)}
          />
          <StyledButtonOr text='>>' />
          <StyledTabButton
            className={activeStep === 3 ? 'active-step' : ''}
            type={'button'}
            content={'Tierwohl'}
            disabled={isTransitioning}
            onClick={() => handleTabChange(3)}
          />
        </StyledButtonGroup>

        <TabContent
          isTransitioning={isTransitioning}
          activeStep={activeStep}
        >
          {!isTransitioning && renderSteps()}
        </TabContent>
      </TabContainer>
    </SummaryOptionsContext.Provider>
  )
}

export default SummaryStep
